import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import React from 'react';

import { DataAPIContext } from '../api';
import DeleteIconButton from './DeleteIconButton';
import { CREATE, GET_LIST } from '../api/dataProvider';

/**
 * Operator-scoped multi-attachment picker (campaigns).
 *
 * Holds a list of Attachments selected on a Campaign and exposes a
 * two-tab modal (Upload new / Browse existing) for adding more. Each
 * item shows as a tile (thumbnail when the file is an image, generic
 * file icon otherwise) with a Remove button.
 *
 * Honest about channels (the helper line below the picker is built by the
 * caller from the enabled channels):
 *   - Email: every attachment is forwarded to mail.template via the
 *     existing ``attachments=`` plumbing — renders as a list of
 *     download links at the bottom of the email body.
 *   - Chat: Twilio Conversations supports one media per message, so the
 *     body goes first and each attachment follows as its own media-only
 *     message (CP-8012).
 *   - SMS: image attachments (incl. .jfif) ride the message as MMS media;
 *     other file types are appended to the text as short links.
 *   - WhatsApp: images / documents (PDF + Office) / MP4 fan out as
 *     media-only follow-ups after the body (one media per message); other
 *     types go as short links.
 */

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|svg|bmp)(\?.*)?$/i;

const isImageUrl = (s?: string | null) => !!s && IMAGE_EXTENSIONS.test(s);

const useStyles = makeStyles((theme: any) => ({
    tile: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1),
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        background: '#fafbff',
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
        maxWidth: 280,
    },
    thumb: {
        width: 48,
        height: 48,
        borderRadius: 4,
        objectFit: 'cover',
        background: '#f5f5f7',
        flexShrink: 0,
    },
    fileIcon: {
        width: 48,
        height: 48,
        borderRadius: 4,
        background: '#eef',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#3b3b5c',
        flexShrink: 0,
    },
    name: {
        maxWidth: 180,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    dropzone: {
        border: '2px dashed #b0b3c0',
        borderRadius: 8,
        padding: theme.spacing(4),
        textAlign: 'center',
        cursor: 'pointer',
        '&:hover': { borderColor: '#7080a0', background: '#fafbff' },
    },
    dropzoneActive: {
        borderColor: '#007AFF',
        background: '#eef4ff',
    },
    selectedTile: {
        outline: '3px solid #007AFF',
        outlineOffset: -3,
    },
}));

interface AttachmentRow {
    id: number;
    name?: string;
    url?: string | null;
    file?: string;
    size?: number | null;
}

interface Item {
    id: number;
    url: string;
    name?: string;
    /** File size in bytes; null/absent = unknown (rows predating size capture). */
    size?: number | null;
}

interface Props {
    value: Item[];
    onChange: (next: Item[]) => void;
    disabled?: boolean;
    label?: string;
    helperText?: string;
}

const AttachmentImagePicker: React.FC<Props> = ({ value, onChange, disabled, label, helperText }) => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext) as any;
    const [open, setOpen] = React.useState(false);
    const [tab, setTab] = React.useState<'upload' | 'browse'>('upload');
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState<string | null>(null);
    const [dragOver, setDragOver] = React.useState(false);
    const [browseRows, setBrowseRows] = React.useState<AttachmentRow[]>([]);
    const [browseLoading, setBrowseLoading] = React.useState(false);
    const [browseError, setBrowseError] = React.useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const items: Item[] = React.useMemo(
        () => (Array.isArray(value) ? value : []),
        [value],
    );
    const selectedIds = React.useMemo(() => new Set(items.map((it) => it.id)), [items]);

    const resetTransientState = () => {
        setUploadError(null);
        setBrowseError(null);
        setDragOver(false);
    };

    const openDialog = () => {
        resetTransientState();
        setTab('upload');
        setOpen(true);
    };

    const closeDialog = () => {
        setOpen(false);
        resetTransientState();
    };

    const addItem = (item: Item) => {
        if (selectedIds.has(item.id)) return;
        onChange([...items, item]);
    };

    const removeItem = (id: number) => {
        onChange(items.filter((it) => it.id !== id));
    };

    const uploadFile = async (file: File) => {
        if (!file) return;
        setUploading(true);
        setUploadError(null);
        try {
            const res = await dataAPI(CREATE, 'attachment', {
                files: { file },
                data: {},
            });
            const body = res?.data || {};
            const id = body.id;
            const url = body.url;
            if (!id || !url) {
                setUploadError('Upload succeeded but server returned no URL.');
                return;
            }
            // Size: prefer the server's stored value, fall back to the local
            // File — the caller uses it to warn about oversized mobile media.
            addItem({ id, url, name: body.name, size: body.size || file.size || null });
            // After upload, leave the modal open on the upload tab so the
            // operator can drop another file. They close manually when done.
        } catch (e: any) {
            setUploadError(e?.body?.message || e?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) uploadFile(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadFile(file);
    };

    React.useEffect(() => {
        if (!open || tab !== 'browse') return;
        let cancelled = false;
        setBrowseLoading(true);
        setBrowseError(null);
        dataAPI(GET_LIST, 'attachment', {
            pagination: { page: 1, perPage: 60 },
            sort: { field: 'id', order: 'DESC' },
            filter: {},
        })
            .then((res: any) => {
                if (cancelled) return;
                setBrowseRows(res?.data || []);
            })
            .catch((e: any) => {
                if (!cancelled) {
                    setBrowseError(e?.message || 'Could not load attachments');
                }
            })
            .finally(() => {
                if (!cancelled) setBrowseLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, tab, dataAPI]);

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label || 'Attachments (optional)'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                {items.map((it) => (
                    <Box key={it.id} className={classes.tile}>
                        {isImageUrl(it.url) ? (
                            <img src={it.url} alt={it.name || ''} className={classes.thumb} />
                        ) : (
                            <Box className={classes.fileIcon}>
                                <InsertDriveFileIcon />
                            </Box>
                        )}
                        <Box className={classes.name}>
                            <Typography variant="body2" noWrap>
                                {it.name || `#${it.id}`}
                            </Typography>
                        </Box>
                        <DeleteIconButton
                            title="Remove attachment"
                            onClick={() => removeItem(it.id)}
                            disabled={disabled}
                            aria-label={`Remove ${it.name || 'attachment'}`}
                        />
                    </Box>
                ))}
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={openDialog}
                    disabled={disabled}
                >
                    <AddIcon fontSize="small"/>&nbsp;Add attachment
                </Button>
            </Box>
            {helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {helperText}
                </Typography>
            )}

            <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>Add attachment</DialogTitle>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
                    <Tab value="upload" label="Upload new" />
                    <Tab value="browse" label="Browse existing" />
                </Tabs>
                <DialogContent dividers sx={{ minHeight: 320 }}>
                    {tab === 'upload' && (
                        <Box
                            className={`${classes.dropzone} ${dragOver ? classes.dropzoneActive : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                onChange={onFileInputChange}
                            />
                            {uploading ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress />
                                    <Typography variant="body2">Uploading…</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <CloudUploadIcon fontSize="large" color="action" />
                                    <Typography variant="body1">
                                        Drag and drop a file here, or click to browse.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Any file type — images preview inline; others show a file tile.
                                    </Typography>
                                </Box>
                            )}
                            {uploadError && (
                                <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                                    {uploadError}
                                </Typography>
                            )}
                        </Box>
                    )}
                    {tab === 'browse' && (
                        <Box>
                            {browseLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            {browseError && (
                                <Typography color="error" variant="body2">
                                    {browseError}
                                </Typography>
                            )}
                            {!browseLoading && !browseError && browseRows.length === 0 && (
                                <Typography color="text.secondary" textAlign="center" py={4}>
                                    No attachments yet. Upload one from the &ldquo;Upload new&rdquo; tab.
                                </Typography>
                            )}
                            {!browseLoading && browseRows.length > 0 && (
                                <ImageList cols={4} rowHeight={140} gap={8}>
                                    {browseRows.map((row) => {
                                        const isSelected = selectedIds.has(row.id);
                                        const imageish = isImageUrl(row.url || row.file || row.name);
                                        return (
                                            <ImageListItem
                                                key={row.id}
                                                onClick={() => {
                                                    if (row.url) {
                                                        addItem({
                                                            id: row.id,
                                                            url: row.url,
                                                            name: row.name,
                                                            size: row.size || null,
                                                        });
                                                    }
                                                }}
                                                sx={{ cursor: 'pointer' }}
                                                className={isSelected ? classes.selectedTile : ''}
                                            >
                                                {imageish && row.url ? (
                                                    <img
                                                        src={row.url}
                                                        alt={row.name || `Attachment ${row.id}`}
                                                        loading="lazy"
                                                        style={{ objectFit: 'cover', height: '100%' }}
                                                    />
                                                ) : (
                                                    <Box className={classes.fileIcon} sx={{ width: '100%', height: '100%', borderRadius: 0 }}>
                                                        <InsertDriveFileIcon fontSize="large" />
                                                    </Box>
                                                )}
                                                <ImageListItemBar
                                                    title={row.name || `#${row.id}`}
                                                    sx={{ '.MuiImageListItemBar-title': { fontSize: 12 } }}
                                                />
                                            </ImageListItem>
                                        );
                                    })}
                                </ImageList>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Done</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AttachmentImagePicker;
